import React from "react";
import { motion } from "framer-motion";
import { AlertTriangle, TrendingUp } from "lucide-react";
import { EASE, fadeUp } from "../../lib/motion";
import { resolveImg } from "../../lib/image";

export const TimesAreChanging = ({ content }) => {
    const t = content?.times_changing || {};
    const words = (t.headline || "Times Are Changing.").split(" ");
    return (
        <section className="relative py-6 md:py-20" data-testid="times-changing-section">
            <div className="mx-auto max-w-[1240px] px-6 md:px-10">
                <motion.span {...fadeUp(0)} className="inline-block text-xs uppercase tracking-[0.24em] text-oth-ink3 font-body" data-testid="times-eyebrow">
                    {t.eyebrow}
                </motion.span>

                <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Left large card */}
                    <div className="lg:col-span-7 surface-card p-8 md:p-12 lg:p-14" data-testid="times-left-card">
                        <h2 className="font-display font-semibold text-[44px] sm:text-[56px] md:text-[72px] leading-[0.98] tracking-[-0.035em]">
                            {words.map((w, i) => (
                                <motion.span
                                    key={`w-${i}-${w}`}
                                    initial={{ opacity: 0, y: 24, filter: "blur(10px)" }}
                                    whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                                    viewport={{ once: true, margin: "-80px" }}
                                    transition={{ duration: 0.9, ease: EASE, delay: i * 0.08 }}
                                    className="inline-block mr-3"
                                >
                                    {w}
                                </motion.span>
                            ))}
                        </h2>
                        <motion.p {...fadeUp(0.4)} className="mt-8 max-w-[520px] font-body text-base md:text-lg text-oth-ink2 leading-relaxed">
                            {t.paragraph}
                        </motion.p>
                        <ul className="mt-8 space-y-4">
                            {(t.bullets || []).map((b, i) => (
                                <motion.li
                                    key={`bullet-${i}-${(typeof b === "string" ? b.slice(0, 24) : "")}`}
                                    {...fadeUp(0.5 + i * 0.1)}
                                    className="flex items-start gap-4 font-body text-oth-ink2"
                                    data-testid={`times-bullet-${i}`}
                                >
                                    <span className="mt-2 w-1.5 h-1.5 rounded-full" style={{ background: "#0B3B60" }} />
                                    <span className="leading-relaxed">{b}</span>
                                </motion.li>
                            ))}
                        </ul>
                    </div>

                    {/* Right column */}
                    <div className="lg:col-span-5 flex flex-col gap-6">
                        {/* family image card */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 1.0, ease: EASE }}
                            className="rounded-[32px] overflow-hidden aspect-[5/4] surface-card"
                            data-testid="times-family-card"
                        >
                            <img src={resolveImg(t.family_image)} alt="Family" className="w-full h-full object-cover slow-zoom" />
                        </motion.div>

                        {/* Dashboard card */}
                        <motion.div
                            {...fadeUp(0.1)}
                            className="surface-card p-7 md:p-8"
                            data-testid="times-dashboard-card"
                        >
                            <div className="flex items-center justify-between">
                                <div className="text-xs uppercase tracking-[0.2em] text-oth-ink3 font-body">{t.dashboard_title}</div>
                                <TrendingUp size={16} className="text-oth-ink3" />
                            </div>
                            <div className="mt-5 font-display text-3xl md:text-4xl font-semibold tracking-tight">{t.dashboard_metric}</div>
                            <div className="mt-1.5 font-body text-sm text-oth-ink3">{t.dashboard_caption}</div>
                            <div className="mt-5 h-16 flex items-center justify-between px-1" data-testid="times-pulse-row" aria-hidden>
                                {Array.from({ length: 15 }).map((_, i) => {
                                    const size = 8 + (i * (48 - 8)) / 14; // 8px → 48px linear progression
                                    return (
                                        <motion.span
                                            key={`pulse-${i}`}
                                            animate={{
                                                scale: [1, 1.18, 1],
                                                opacity: [0.15, 1, 0.15],
                                            }}
                                            transition={{
                                                duration: 1.0,
                                                times: [0, 0.4, 1],
                                                ease: EASE,
                                                repeat: Infinity,
                                                repeatDelay: 0,
                                                delay: (i * 1.0) / 15,
                                            }}
                                            className="rounded-full"
                                            style={{
                                                background: "#0B3B60",
                                                display: "inline-block",
                                                width: `${size}px`,
                                                height: `${size}px`,
                                                willChange: "transform, opacity",
                                            }}
                                        />
                                    );
                                })}
                            </div>
                        </motion.div>

                        {/* Alert card */}
                        <motion.div
                            {...fadeUp(0.2)}
                            className="surface-card p-6 md:p-7 flex items-start gap-4 soft-pulse"
                            data-testid="times-alert-card"
                        >
                            <span className="mt-0.5 w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(11,59,96,0.08)" }}>
                                <AlertTriangle size={18} style={{ color: "#0B3B60" }} />
                            </span>
                            <div>
                                <div className="font-display text-base font-semibold">{t.alert_title}</div>
                                <div className="mt-1 font-body text-sm text-oth-ink3 leading-relaxed">{t.alert_message}</div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
};
